import { useCallback, useContext, useState } from 'react';
import { AxiosError } from 'axios';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { getAxiosErrorMessageCode } from 'axiosUtils';
import { ERROR_TYPES } from 'request-responses';

type UseStarDocumentProps = {
  node: DataRoomNode;
  updateNode: (node: DataRoomNode) => void;
  onStarLimitReached: () => void;
};

/**
 * Toggles the starred state of a data room node. Shared by the Files page rows
 * and the search results so both behave identically, including opening the
 * "unstar documents" flow when the starred-files limit is exceeded.
 */
export default function useStarDocument({
  node,
  updateNode,
  onStarLimitReached,
}: UseStarDocumentProps) {
  const [isStarring, setIsStarring] = useState(false);

  const { _id, isStarred } = node;

  const { popServerError } = useContext(PopNotificationsContext);

  const handleStarDocument = useCallback(async () => {
    try {
      setIsStarring(true);
      const newIsStarred = !isStarred;

      await dataRoomApi.nodes[newIsStarred ? 'starDocument' : 'unStarDocument'](
        _id,
      );

      updateNode({ ...node, isStarred: newIsStarred });
    } catch (e) {
      const errorCode = getAxiosErrorMessageCode(e as AxiosError);

      if (errorCode === ERROR_TYPES.DATA_ROOM_STARRED_FILES_LIMIT_EXCEEDED)
        onStarLimitReached();
      else popServerError(e);
    } finally {
      setIsStarring(false);
    }
  }, [_id, isStarred, node, updateNode, onStarLimitReached, popServerError]);

  return { isStarring, handleStarDocument };
}
